<template>
  <div class="flow-details flow-navigation">
    <div class="subtitle">
      {{ header }}
    </div>
    <div class="p-16 flow-details__wrap">
      <div>
        <div
          class="flow-navigation__navigation-item flow-navigation__navigation-item--general"
          :class="{
            'flow-navigation__navigation-item--active': activeItem === null,
            'flow-navigation__navigation-item--has-error': isGeneralError
          }"
          @click="selectFlowSettings"
        >
          <span class="flow-navigation__general-navigation-item-text">
            <ui-icon class="flow-navigation__general-navigation-item-icon" size="14" icon="fas fa-sitemap"></ui-icon>
            {{ nameDescription }}
          </span>
        </div>
      </div>

      <div v-show="!navigationHidden">
        <v-list
          density="compact"
          v-model:opened="openedGroups"
        >
          <v-list-group
            v-for="(rule, ruleIndex) in form.rules"
            :key="ruleIndex"
            :value="ruleIndex"
            fluid
            collapse-icon=""
            expand-icon=""
          >
            <template #activator="{ isOpen }">
              <v-list-item
                class="flow-navigation__navigation-item"
                :class="{
                  'flow-navigation__navigation-item--active': rule === activeItem,
                  'flow-navigation__navigation-item--expanded': isOpen,
                  'flow-navigation__navigation-item--has-error': navigationErrorsRulesIndex[ruleIndex] && navigationErrorsRulesIndex[ruleIndex].hasError,
                  'flow-navigation__navigation-item--has-path-error': navigationErrorsRulesIndex[ruleIndex] && navigationErrorsRulesIndex[ruleIndex].hasPathError,
                }"
                @click="setRule(rule, formErrors.rules[ruleIndex])"
              >
                <flow-navigation-rule
                  :item="rule"
                  :rule-index="ruleIndex"
                  :length="form.rules.length"
                  :rule-error="navigationErrors[ruleIndex]"
                  @move-rule="handleMoveRule"
                  @expand="toggleGroup"
                ></flow-navigation-rule>
              </v-list-item>
            </template>

            <flow-navigation-path
              v-for="(path, pathIndex) in rule.paths"
              :key="path.name"
              class="flow-navigation__navigation-item flow-navigation__navigation-item--path"
              :class="{
                'flow-navigation__navigation-item--active': path === activeItem,
                'flow-navigation__navigation-item--has-error': navigationErrorsPathsIndex[getNavigationIndexKey(ruleIndex, pathIndex)],
              }"
              :path="path"
              :paths="rule.paths"
              :rule-status="rule.status"
              :active-item="activeItem"
              :rule-index="ruleIndex"
              :path-index="pathIndex"
              @select="setPath(path, rule)"
            ></flow-navigation-path>

            <v-list-item
              class="flow-navigation__navigation-item flow-navigation__navigation-item--path"
              @click="emit('add-path', rule, setPath)"
            >
              <ui-icon :color="$colors.green" size="14" icon="fas fa-plus"></ui-icon>&nbsp;
              {{ t('Add new path') }}
            </v-list-item>
          </v-list-group>

          <v-list-item
            class="flow-navigation__navigation-item"
            @click="emit('add-rule')"
          >
            <ui-icon :color="$colors.green" size="14" icon="fas fa-plus"></ui-icon>&nbsp;
            {{ t('Add new rule') }}
          </v-list-item>
        </v-list>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import FlowNavigationRule from './flow-navigation-rule.vue';
import FlowNavigationPath from './flow-navigation-path.vue';
import { useFlowNavigation } from './useFlowNavigation';

const $colors = useColors();

const props = defineProps<{
  form: {
    name?: string;
    rules: Array<{
      default?: boolean;
      name?: string;
      status?: string;
      conditions?: Record<string, unknown>;
      paths: unknown[];
      active?: boolean;
    }>;
  };
  countryName?: string;
  header: string;
  activeRule: {
    default?: boolean;
    name?: string;
    conditions?: Record<string, unknown>;
    paths: unknown[];
  };
  formErrors: { rules: unknown[] };
  errorsRaw: unknown[][] | null;
  errorConditionPrefix?: string[];
}>();

const emit = defineEmits<{
  'update:view': [value: string];
  'set-placeholder': [value: string];
  'add-path': [rule: typeof props.form.rules[number], callback: (item: unknown, rule: typeof props.form.rules[number], errors?: unknown) => void];
  'add-rule': [];
  'set-rule': [rule: typeof props.form.rules[number]];
  'set-path': [path: unknown];
  'set-errors': [errors: unknown];
}>();

const {
  t,
  activeItem,
  navigationHidden,
  nameDescription,
  isGeneralError,
  navigationErrors,
  navigationErrorsRulesIndex,
  navigationErrorsPathsIndex,
  openedGroups,
  getNavigationIndexKey,
  handleMoveRule,
  toggleGroup,
  selectFlowSettings,
  setRule,
  setPath,
  openNavigation,
} = useFlowNavigation(props, emit);

defineExpose({
  openNavigation,
  selectFlowSettings,
  setPath,
  setRule,
});
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';
</style>

<style lang="scss">
.flow-navigation  {
  flex-grow: 0;
  flex-shrink: 0;
  height:100%;
  overflow-y:auto;

  width: 400px;

   @media (max-width: 960px) {
    width: 300px;
  }

  .v-list {
    background: transparent;
    padding: 0;
  }

  // Hide Vuetify default expand/collapse icons — custom chevrons are used
  .v-list-group__header .v-list-item__append {
    display: none !important;
  }

  // Keep rule rows single-line like develop's v-list__tile__title
  .v-list-item {
    .v-list-item__content {
      overflow: hidden;
      min-width: 0;
    }
  }

  .v-list-item--one-line {
    .v-list-item__content {
      white-space: nowrap;
    }
  }

  // Open-group separators; margin collapses adjacent borders into one line
  .v-list-group.v-list-group--open {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    margin-bottom: -1px;
    position: relative;
    z-index: 1;
  }

  .v-list-group__items {
    .v-list-item {
      padding-inline-start: 45px !important;
    }
  }

  &__general-navigation-item-text {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-x: hidden;
    overflow-y: hidden;
  }

  &__rule-actions {
    margin-right: 10px;
    display: inline-block
  }

  &__rules-chevron-wrapper {
    display: inline-block;
    width: 18px;
  }

  &__rule-action-move + &__rule-action-move {
    margin-left: 5px;
  }

  &__navigation-item {
    $active-color: var(--color-gray5);
    $error-color: var(--color-pink);
    $active-error-color: var(--color-pink);

    min-height: 40px !important;
    padding-inline: 10px !important;

    &--general {
      display: flex;
      align-items: center;
      height: 40px;
      padding: 0 16px 0 10px;
      cursor: pointer;
      font-size: 13px;
    }

    &.v-list-item:hover {
      background: var(--color-white20)!important;
    }

    &--path.v-list-item {
      padding-inline-start: 45px !important;
    }

    &--has-error {
      background-color: $error-color;
    }

    &--has-path-error {
      background-color: $error-color;
    }

    &--active, &:hover {
      color: var(--color-graphite);
      background-color: $active-color;
    }

    &--expanded {
      .flow-navigation-rule__chevron-down {
        display: inline-flex;
      }

      .flow-navigation-rule__chevron-right {
        display: none;
      }
    }

    @at-root #{selector-append(& + '--expanded', & + '--has-path-error')} {
      background-color: var(--color-white);
    }

    @at-root #{selector-append(& + '--expanded', & + '--has-error')} {
       background-color: $error-color;
    }

    @at-root #{selector-append(& + '--active', & + '--has-error')},
    #{selector-append(& + ':hover', & + '--has-error')},
    #{selector-append(& + '--active', & + '--has-path-error')},
    #{selector-append(& + ':hover', & + '--has-path-error')}  {
      background-color: $active-error-color;
    }

    @at-root #{selector-append(& + '--active', & + '--has-path-error', & + '--expanded')},
    #{selector-append(& + ':hover', & + '--has-path-error', & + '--expanded')}  {
      background-color: $active-color;
    }

    @at-root #{selector-append(& + '--active', & + '--has-error', & + '--expanded')},
    #{selector-append(& + ':hover', & + '--has-error', & + '--expanded')}  {
      background-color: $active-error-color;
    }
  }

  & &__general-navigation-item-icon {
    color: var(--color-black);
    padding-right: 6px;
  }
}

</style>
